import { useState, useEffect, useRef } from 'react'
import { Search, ChevronDown, Check } from 'lucide-react'
import { Cliente } from '../../types/offerte'

interface ClienteSelectorProps {
  clienti: Cliente[]
  selectedId: string
  onChange: (clienteId: string) => void
  disabled?: boolean
}

export function ClienteSelector({ clienti, selectedId, onChange, disabled }: ClienteSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  const selectedCliente = clienti.find(c => c.id === selectedId)

  const filteredClienti = clienti.filter(c =>
    c.attivo && c.ragione_sociale.toLowerCase().includes(search.toLowerCase())
  )

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-3 py-2 border border-gray-200 rounded-lg text-left ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-300'
        } focus:outline-none focus:ring-2 focus:ring-primary-500`}
      >
        <span className={selectedCliente ? 'text-gray-900' : 'text-gray-400'}>
          {selectedCliente?.ragione_sociale || 'Seleziona cliente...'}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca cliente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-48 overflow-y-auto">
            {filteredClienti.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                Nessun cliente trovato
              </div>
            ) : (
              filteredClienti.map((cliente) => (
                <button
                  key={cliente.id}
                  type="button"
                  onClick={() => {
                    onChange(cliente.id)
                    setIsOpen(false)
                    setSearch('')
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                    cliente.id === selectedId ? 'bg-primary-50' : ''
                  }`}
                >
                  <span className="font-medium text-gray-900">{cliente.ragione_sociale}</span>
                  {cliente.id === selectedId && (
                    <Check className="w-4 h-4 text-primary-600" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
